import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2, ArrowLeft } from "lucide-react";

/**
 * WebOTP API - Chrome / Android WebView (incl. Capacitor) + Safari/iOS.
 * SMS template must end with `\n\n@elefa.com.au #{{code}}` for the Android
 * system sheet ("Verify with elefa.tel - 123456") to appear on tap.
 * Until that template ships, the call below is harmless (auto-aborts).
 */
declare global {
  interface Window {
    OTPCredential?: unknown;
  }
}

interface OtpVerifyInputProps {
  phoneNumber: string;
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  onBack: () => void;
  isVerifying: boolean;
  isResending: boolean;
  isComplete?: boolean;
}

export function OtpVerifyInput({
  phoneNumber,
  onVerify,
  onResend,
  onBack,
  isVerifying,
  isResending,
  isComplete = false,
}: OtpVerifyInputProps) {
  const { t } = useTranslation("common");
  const [code, setCode] = useState("");
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const lastSubmittedCodeRef = useRef<string | null>(null);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown <= 0) {
      setCanResend(true);
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  // Auto-verify when code is complete - with duplicate prevention
  useEffect(() => {
    if (
      code.length === 6 && 
      !isVerifying && 
      !isComplete &&
      lastSubmittedCodeRef.current !== code
    ) {
      lastSubmittedCodeRef.current = code;
      onVerify(code);
    }
  }, [code, isVerifying, isComplete, onVerify]);

  // Android WebOTP API - listens for the incoming SMS and auto-fills the code
  // when the user taps the system sheet. Silently no-ops where unsupported
  // (iOS already provides the keyboard chip via autoComplete="one-time-code").
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("OTPCredential" in window)) return;
    if (isComplete) return;

    const ac = new AbortController();
    (navigator.credentials as unknown as {
      get: (o: unknown) => Promise<{ code?: string } | null>;
    })
      .get({ otp: { transport: ["sms"] }, signal: ac.signal })
      .then((cred) => {
        const otp = cred?.code?.replace(/\D/g, "").slice(0, 6);
        if (otp && otp.length === 6) setCode(otp);
      })
      .catch(() => { /* user dismissed or aborted - silent */ });

    return () => ac.abort();
  }, [isComplete]);

  const handleResend = async () => {
    if (!canResend || isResending) return;
    
    await onResend();
    setCountdown(60);
    setCanResend(false);
    setCode("");
  };

  // Format phone for display
  const formatPhone = (phone: string) => {
    // Show last 4 digits masked
    if (phone.length > 4) {
      return phone.slice(0, -4).replace(/\d/g, '•') + phone.slice(-4);
    }
    return phone;
  };

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        disabled={isVerifying}
      >
        <ArrowLeft className="h-4 w-4" />
        Change number
      </button>

      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-xl font-semibold">{t("web.auth.enterCode")}</h2>
        <p className="text-sm text-muted-foreground">
          {t("web.auth.sentCodeTo")}{" "}
          <span className="font-mono font-medium text-foreground">
            {formatPhone(phoneNumber)}
          </span>
        </p>
      </div>

      {/* OTP Input */}
      <div className="flex justify-center">
        <InputOTP
          maxLength={6}
          value={code}
          onChange={setCode}
          disabled={isVerifying}
          autoComplete="one-time-code"
          inputMode="numeric"
          pattern="[0-9]*"
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={1} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={2} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={3} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={4} className="w-12 h-14 text-xl" />
            <InputOTPSlot index={5} className="w-12 h-14 text-xl" />
          </InputOTPGroup>
        </InputOTP>
      </div>

      {/* Loading indicator */}
      {isVerifying && (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("web.auth.verifying")}
        </div>
      )}

      {/* Resend button */}
      <div className="text-center">
        {canResend ? (
          <Button
            variant="ghost"
            onClick={handleResend}
            disabled={isResending}
            className="text-primary"
          >
            {isResending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {t("web.auth.sendingShort")}
              </>
            ) : (
              t("web.auth.resendCode")
            )}
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground">
            {t("web.auth.resendIn")}{" "}
            <span className="font-mono font-medium">{countdown}s</span>
          </p>
        )}
      </div>

      {/* Help text */}
      <p className="text-xs text-center text-muted-foreground">
        {t("web.auth.didntReceive")}
      </p>
    </div>
  );
}
